import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { renderPaginationRange } from "../../../app/utils/paginationRange";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import { formSnapshot } from "../../../../../../../shared/frontend/forms/formSnapshot";
import type { ProColumnsWithMeasure,SettingsListActionRef } from "../../../components/SettingsListPage";
import {typography as designTypography,borderRadius as designBorderRadius,cardDefaults,spacing} from "../../../app/design-system";
import { CreditCardOutlined,DollarOutlined,EditOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MerchantPaymentLinkAction } from "../../../../../../../shared/frontend/payments/MerchantPaymentLinkAction";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { Button, Col, Descriptions, Flex, Form, Grid, InputNumber, Row, Select, Tag, Typography } from "../../../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import { CONTRIBUTION_STATUS_CANCELLED,CONTRIBUTION_STATUS_DUE,CONTRIBUTION_STATUS_IN_DEBT_COLLECTION,CONTRIBUTION_STATUS_OVERDUE,CONTRIBUTION_STATUS_PAID,CONTRIBUTION_STATUS_RESERVED } from "../../../app/constants/contributionCalendarStatus";
import { OPEN_ITEM_STATUS_OPEN } from "../../../app/constants/openItemStatus";
import { SURCHARGE_TYPE_FIXED_PER_INSTALLMENT,SURCHARGE_TYPE_PERCENT_OF_TOTAL } from "../../../app/constants/paymentPlanSurchargeType";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatDateTimeIso,formatMoney,formatPercent } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { formatCycleUnitLabel } from "../../../app/utils/cycleUnitI18n";
import { paymentMethodDefinitionSelectOptions,paymentMethodLabel } from "../../../app/utils/paymentMethod";
import { PAYMENT_METHOD_LABELS } from "../../../app/utils/paymentMethodCodes";
import { DrawerSelectionList } from "../../../components/DrawerSelectionList";
import { ContributionCancellationAmount, OpenItemCancellationAction } from "../../../components/OpenItemCancellationAction";
import { FormDrawer } from "../../../components/FormDrawer";
import type { PaymentItem } from "../../../components/PaymentDrawer";
import { SettingsListPage } from "../../../components/SettingsListPage";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type { ContributionCalendarRow,PaymentPlanDto,PersonOpenItemRow } from "./personDetailTypes";
import { computeInstallmentsPreviewWithRounding,contributionStatusFromDueDateForDisplay,formatDateOnlyIso,isContributionWaived } from "./personDetailUtils";
import { PERSON_DETAIL_LOOKUP_STALE_TIME_MS,personDetailQueryKeys } from "./personDetailQueryKeys";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { MIN_PAYABLE_AMOUNT } from "./personAmountThresholds";

const OPEN_ITEM_DISPLAY_STATUS = {
  paid: "paid", cancelled: "cancelled", debtCollection: "debt-collection", partialPaid: "partial-paid",
  storno: "storno", future: "future", open: "open"
} as const;
const MIN_INSTALLMENT_COUNT = 2;
const MAX_INSTALLMENT_COUNT = 24;
const SURCHARGE_PERCENT_DECIMALS = 2;
const DEFAULT_INSTALLMENT_COUNT = 2;
const PAYMENT_PLAN_NAME_COLUMN_WIDTH = 200;
const INSTALLMENT_COUNT_COLUMN_WIDTH = 120;
const TOTAL_AMOUNT_COLUMN_WIDTH = 120;
const CREATED_AT_COLUMN_WIDTH = 140;
const ACTIONS_COLUMN_WIDTH = 200;
const DUE_DATE_COLUMN_WIDTH = 160;
const AMOUNT_COLUMN_WIDTH = 140;
const DESCRIPTION_COLUMN_WIDTH = 300;
const PAID_AMOUNT_COLUMN_WIDTH = 140;
const OPEN_AMOUNT_COLUMN_WIDTH = 140;
const PAYMENT_METHOD_COLUMN_WIDTH = 140;
const STATUS_COLUMN_WIDTH = 120;

type InstallmentRow = {
  id: string;
  openItemId: string;
  paymentPlanId: string;
  paymentPlanName: string;
  installmentCount: number;
  totalAmount: number;
  createdAt: string;
};

const INSTALLMENT_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "paymentPlanName", defaultVisible: true },
  { key: "installmentCount", defaultVisible: true },
  { key: "totalAmount", defaultVisible: true },
  { key: "createdAt", defaultVisible: true },
];

type OpenItemsInstallmentFormValues = {
  paymentPlanId: string | null;
  installmentCount: number | null;
  startDate: Dayjs | null;
};

type OpenItemApiRow = {
  contributionSource?: { id: string; contractPackageId: string; isManual: boolean; paymentMethod: number } | null;
  displayStatus?: string | null;
  correctsOpenItemId?: string | null;
  cancelledAmount?: number;
  id: string;
  amount: number;
  paidAmount?: number | null;
  openAmount?: number | null;
  description: string;
  dueDate: string;
  bookedAt?: string | null;
  status: number;
  paymentMethod?: number | null;
  paymentId?: string | null;
  contractPackageName?: string | null;
  submittedSepaDirectDebitRunId?: string | null;
  submittedToSepaAt?: string | null;
};

function formatOpenItemOpenAmount(row: PersonOpenItemRow, text: I18nContextValue["text"], units: UnitsSettings): React.ReactNode {
  const openAmount = Number(row.openAmount ?? row.amount ?? 0);
  if (row.displayStatus === OPEN_ITEM_DISPLAY_STATUS.storno || Number(row.amount ?? 0) < -MIN_PAYABLE_AMOUNT || openAmount < -MIN_PAYABLE_AMOUNT) {
    return `${text("Credit amount")}: ${formatMoney(Math.abs(openAmount), units)}`;
  }
  return formatMoney(openAmount, units);
}

type Props = {
  api: Pick<ApiClient, "get" | "post" | "put">;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  openItemsStudioIdsParam?: string;
  canAgreeInstallment: boolean;
  canWriteFinance: boolean;
  dateInputFormat: string | string[];
  openItemsRef: React.RefObject<SettingsListActionRef | null>;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  selectedRows: PersonOpenItemRow[];
  setSelectedRows: React.Dispatch<React.SetStateAction<PersonOpenItemRow[]>>;
  setPaymentDrawerItems: React.Dispatch<React.SetStateAction<PaymentItem[]>>;
  setPaymentDrawerSelectedItems: React.Dispatch<React.SetStateAction<PaymentItem[]>>;
  setPaymentDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setContributionEdit?: (value: ContributionCalendarRow | null) => void;
  contributionEditForm?: FormInstance<{ amount?: number; paymentMethod?: number }>;
  setContributionEditOpen?: (value: boolean) => void;
  onOpenContributionEdit?: (row: ContributionCalendarRow) => void;
};

export function PersonOpenItemsTab({
  api,
  text,
  units,
  personId,
  openItemsStudioIdsParam,
  canAgreeInstallment,
  canWriteFinance,
  dateInputFormat,
  openItemsRef,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  setPaymentDrawerItems,
  setPaymentDrawerSelectedItems,
  setPaymentDrawerOpen,
  setContributionEdit,
  contributionEditForm,
  setContributionEditOpen,
  onOpenContributionEdit
}: Props) {
  const queryClient = useQueryClient();
  const feedback = useFeedback();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [openItemsForInstallment, setOpenItemsForInstallment] = React.useState<PersonOpenItemRow[]>([]);
  const [openItemsInstallmentDrawerItems, setOpenItemsInstallmentDrawerItems] = React.useState<PersonOpenItemRow[]>([]);
  const [openItemsInstallmentOpen, setOpenItemsInstallmentOpen] = React.useState(false);
  const [openItemsInstallmentSaving, setOpenItemsInstallmentSaving] = React.useState(false);
  const [openItemsInstallmentForm] = Form.useForm<OpenItemsInstallmentFormValues>();
  const installmentFields = Form.useWatch(formSnapshot, { form: openItemsInstallmentForm, preserve: true });
  const { dirty: installmentDirty, markSaved: installmentMarkSaved } = useDirtySnapshot({ fields: installmentFields, extra: { selected: openItemsForInstallment.map(row => row.id) } });
  const installmentInitializeDirty = useDrawerBaselineEvent(() => installmentMarkSaved({ fields: formSnapshot(openItemsInstallmentForm.getFieldsValue(true)), extra: { selected: openItemsForInstallment.map(row => row.id) } }));
  useDrawerBaselineEffect(() => { if (openItemsInstallmentOpen) installmentInitializeDirty(); }, [openItemsInstallmentOpen]);

  const [openItemsInstallmentValues, setOpenItemsInstallmentValues] = React.useState<Partial<OpenItemsInstallmentFormValues>>({});
  const [visibleOpenItems, setVisibleOpenItems] = React.useState<PersonOpenItemRow[]>([]);
  const payableItems = useQuery({ queryKey: ["person", personId, "merchant-payment-options"],
    queryFn: () => api.get<string[]>(`/api/v1/persons/${personId}/merchant-payment-links/options`), enabled: canWriteFinance && visibleOpenItems.length > 0 });
  const [paymentPlans, setPaymentPlans] = React.useState<PaymentPlanDto[]>([]);
  const [installments, setInstallments] = React.useState<InstallmentRow[]>([]);
  const [paymentMethodDefs, setPaymentMethodDefs] = React.useState<Array<{ paymentMethodType: number; name: string; isActive?: boolean }>>([]);

  // Tenant-Zahlungsarten laden, damit die Spalte den Namen (nicht die Typ-Zahl) zeigt.
  React.useEffect(() => {
    let cancelled = false;
    void api.get<Array<{ paymentMethodType: number; name: string; isActive?: boolean }>>("/api/v1/payment-method-definitions")
      .then((list) => { if (!cancelled) setPaymentMethodDefs(list || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  const paymentMethodNameByType = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const m of paymentMethodDefs || []) {
      map[m.paymentMethodType] = String(m.name || "").trim() || PAYMENT_METHOD_LABELS[m.paymentMethodType] || String(m.paymentMethodType);
    }
    return map;
  }, [paymentMethodDefs]);

  // Bulk-Zahlart für offene Beiträge
  const [bulkZahlartOpen, setBulkZahlartOpen] = React.useState(false);
  const [bulkZahlartSaving, setBulkZahlartSaving] = React.useState(false);
  const financeOperation = useFinanceOperation(personId);
  const [bulkZahlartItems, setBulkZahlartItems] = React.useState<PersonOpenItemRow[]>([]);
  const [bulkZahlartSelected, setBulkZahlartSelected] = React.useState<PersonOpenItemRow[]>([]);
  const [bulkZahlartForm] = Form.useForm<{ paymentMethod?: number }>();
  const paymentMethodFields = Form.useWatch(formSnapshot, { form: bulkZahlartForm, preserve: true });
  const { dirty: paymentMethodDirty, markSaved: paymentMethodMarkSaved } = useDirtySnapshot({ fields: paymentMethodFields, extra: { selected: bulkZahlartSelected.map(row => row.id) } });
  const paymentMethodInitializeDirty = useDrawerBaselineEvent(() => paymentMethodMarkSaved({ fields: formSnapshot(bulkZahlartForm.getFieldsValue(true)), extra: { selected: bulkZahlartSelected.map(row => row.id) } }));
  useDrawerBaselineEffect(() => { if (bulkZahlartOpen) paymentMethodInitializeDirty(); }, [bulkZahlartOpen]);

  const bulkWatchedPaymentMethod = Form.useWatch("paymentMethod", bulkZahlartForm);

  /**
   * Zahlart einer Zeile änderbar? Teilzahlungen sind erlaubt (nur die Zahlart, nicht der Betrag).
   * Ausgeschlossen: voll bezahlt/nichts offen, in einem SEPA-Lauf eingereicht, erlassen, im Inkasso.
   */
  const isZahlartEditableRow = React.useCallback((r: PersonOpenItemRow): boolean => {
    if (r.sourceType !== "contribution") return false;
    if (Number(r.openAmount ?? r.amount ?? 0) <= MIN_PAYABLE_AMOUNT) return false;
    if (r.submittedSepaDirectDebitRunId) return false;
    if (isContributionWaived({ status: Number(r.status || 0), isManual: r.contributionIsManual === true })) return false;
    if (Number(r.status || 0) === CONTRIBUTION_STATUS_RESERVED) return false;
    if (Number(r.status || 0) === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION) return false;
    return true;
  }, []);

  const reloadInstallments = React.useCallback(() => {
    void queryClient
      .fetchQuery({
        queryKey: ["person", personId, "open-item-installments"],
        queryFn: async () => {
          const list = (await api.get<InstallmentRow[]>(`/api/v1/open-items/${encodeURIComponent(personId)}/installments`)) || [];
          return list
            .map((x) => ({
              id: String(x.id),
              openItemId: String(x.openItemId),
              paymentPlanId: String(x.paymentPlanId),
              paymentPlanName: String(x.paymentPlanName || ""),
              installmentCount: Number(x.installmentCount || 0),
              totalAmount: Number(x.totalAmount || 0),
              createdAt: String(x.createdAt || "")
            }))
            .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
        },
        staleTime: 0
      })
      .then(setInstallments)
      .catch(() => {});
  }, [api, personId, queryClient]);

  React.useEffect(() => {
    let cancelled = false;
    void queryClient
      .fetchQuery({
        queryKey: personDetailQueryKeys.lookups.paymentPlans,
        queryFn: async () => {
          const list = (await api.get<PaymentPlanDto[]>("/api/v1/payment-plans")) || [];
          return list.filter((p) => p.isActive);
        },
        staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
      })
      .then((list) => {
        if (!cancelled) setPaymentPlans(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, queryClient]);

  React.useEffect(() => {
    reloadInstallments();
  }, [reloadInstallments]);

  const saveOpenItemInstallment = async () => {
    const v = await openItemsInstallmentForm.validateFields();
    const items = openItemsForInstallment.filter((it) => Number(it.status || 0) === OPEN_ITEM_STATUS_OPEN && Number(it.openAmount ?? it.amount ?? 0) > MIN_PAYABLE_AMOUNT);
    if (items.length === 0) throw new Error(text("Please select at least one open item"));
    if (!v.paymentPlanId) throw new Error(text("Please select a payment plan"));
    if (!v.installmentCount) throw new Error(text("Please enter the number of installments"));

    const plan = paymentPlans.find((p) => String(p.id) === v.paymentPlanId);
    if (!plan) throw new Error(text("Payment plan not found"));
    const minC = MIN_INSTALLMENT_COUNT;
    const maxC = MAX_INSTALLMENT_COUNT;
    if (v.installmentCount < minC || v.installmentCount > maxC) {
      throw new Error(text("Number of installments must be between").replace("{0}", String(minC)).replace("{1}", String(maxC)));
    }

    setOpenItemsInstallmentSaving(true);
    try {
      const startDateIso = formatDateOnlyIso(v.startDate);
      if (!startDateIso) throw new Error(text("Start date is required"));
      await financeOperation.execute(async (committed) => {
      for (const item of items) {
        await api.post(`/api/v1/open-items/${encodeURIComponent(item.id)}/installment`, {
          paymentPlanId: v.paymentPlanId,
          installmentCount: v.installmentCount,
          startDate: startDateIso
        });
        committed();
      }
      feedback.success(items.length === 1 ? text("Installment plan created") : text("Installment plan created for items").replace("{0}", String(items.length)));
      setOpenItemsInstallmentOpen(false);
      setOpenItemsInstallmentValues({});
      }, async () => { await reloadInstallments(); });
    } finally {
      setOpenItemsInstallmentSaving(false);
    }
  };

  const openItemsWatchedPlanId = openItemsInstallmentValues.paymentPlanId;
  const openItemsWatchedInstallmentCount = openItemsInstallmentValues.installmentCount;
  const openItemsSelectedPlan = React.useMemo(() => {
    const id = openItemsWatchedPlanId ? String(openItemsWatchedPlanId) : "";
    return id ? paymentPlans.find((p) => String(p.id) === id) || null : null;
  }, [paymentPlans, openItemsWatchedPlanId]);

  const openItemsInstallmentPreview = React.useMemo(() => {
    if (!openItemsSelectedPlan || openItemsForInstallment.length === 0) return null;
    const basePrice = openItemsForInstallment.reduce((sum, item) => sum + Number(item.openAmount ?? item.amount ?? 0), 0);
    if (!Number.isFinite(basePrice) || basePrice <= 0) return null;
    const count = Number(openItemsWatchedInstallmentCount ?? openItemsSelectedPlan.minInstallmentCount ?? 1);
    if (!Number.isFinite(count) || count < 1) return null;
    const preview = computeInstallmentsPreviewWithRounding(openItemsSelectedPlan, basePrice, count);
    const cycle = count <= 1 ? text("Cycle unit once") : formatCycleUnitLabel(openItemsSelectedPlan.cycleUnit, text);
    const surchargeType = Number(openItemsSelectedPlan.surchargeType || SURCHARGE_TYPE_FIXED_PER_INSTALLMENT);
    const surchargeValue = Number(openItemsSelectedPlan.surchargeValue || 0);
    let surchargeLabel = "—";
    if (count > 1 && surchargeValue > 0) {
      if (surchargeType === SURCHARGE_TYPE_FIXED_PER_INSTALLMENT) surchargeLabel = formatMoney(surchargeValue, units) + text("per installment");
      else if (surchargeType === SURCHARGE_TYPE_PERCENT_OF_TOTAL) surchargeLabel = `${formatPercent(surchargeValue, units, SURCHARGE_PERCENT_DECIMALS)} ${text("of total")}`;
    }
    return { ...preview, basePrice, cycle, surchargeLabel, selectedItemCount: openItemsForInstallment.length };
  }, [openItemsSelectedPlan, openItemsForInstallment, openItemsWatchedInstallmentCount, text, units]);

  const openItemsRequest = React.useCallback(async (): Promise<{ data: PersonOpenItemRow[]; success: boolean; total: number }> => {
    const openItemsPath = (() => {
      const q = new URLSearchParams();
      q.set("personId", personId);
      q.set("includeContributionSource", "true");
      if (openItemsStudioIdsParam) q.set("studioIds", openItemsStudioIdsParam);
      return `/api/v1/open-items?${q.toString()}`;
    })();
    const apiOpenItems = await queryClient.fetchQuery({
        queryKey: [...personDetailQueryKeys.person.openItems(personId), openItemsStudioIdsParam || "", "contribution-source"],
        queryFn: () => api.get<OpenItemApiRow[]>(openItemsPath),
        staleTime: 0
      });
    const data: PersonOpenItemRow[] = (apiOpenItems || []).map((item) => {
      const contribution = item.contributionSource;
      const isContribution = Boolean(contribution);
      return {
        id: item.id,
        displayStatus: item.displayStatus,
        correctsOpenItemId: item.correctsOpenItemId,
        cancelledAmount: item.cancelledAmount,
        contractPackageId: contribution?.contractPackageId ?? null,
        amount: item.amount,
        description: item.description || text("Open item"),
        dueDate: item.dueDate,
        bookedAt: item.bookedAt ?? null,
        status: item.status,
        paidAmount: item.paidAmount ?? 0,
        openAmount: item.openAmount ?? (item.amount - (item.paidAmount || 0)),
        paymentMethod: isContribution ? contribution?.paymentMethod : (item.paymentMethod ?? undefined),
        paymentId: item.paymentId ?? null,
        contractPackageName: item.contractPackageName ?? undefined,
        submittedSepaDirectDebitRunId: item.submittedSepaDirectDebitRunId ?? null,
        submittedToSepaAt: item.submittedToSepaAt ?? null,
        sourceType: isContribution ? "contribution" : "openItem",
        sourceId: isContribution ? contribution?.id : item.id,
        contributionIsManual: isContribution ? Boolean(contribution?.isManual) : undefined
      };
    });
    return { data, success: true, total: data.length };
  }, [api, openItemsStudioIdsParam, personId, queryClient, text]);

  const openBulkZahlart = React.useCallback(async () => {
    const res = await openItemsRequest();
    const eligible = (res.data || []).filter(isZahlartEditableRow);
    if (eligible.length === 0) {
      feedback.warning(text("No open contributions to change payment method"));
      return;
    }
    // Nur die vorher angeklickten Zeilen vorauswählen; ohne Vorauswahl bleibt nichts markiert.
    const selectedIds = new Set((selectedRows ?? []).map((r) => String(r.id)));
    const initialSelection = eligible.filter((item) => selectedIds.has(String(item.id)));
    // Gemeinsame Zahlungsart vorbelegen, falls durchgängig gleich – sonst leer.
    const methods = new Set(initialSelection.map((r) => Number(r.paymentMethod || 0)).filter((m) => m > 0));
    const commonMethod = methods.size === 1 ? [...methods][0] : undefined;

    setBulkZahlartItems(eligible);
    setBulkZahlartSelected(initialSelection);
    bulkZahlartForm.resetFields();
    bulkZahlartForm.setFieldsValue({ paymentMethod: commonMethod });
    setBulkZahlartOpen(true);
  }, [openItemsRequest, isZahlartEditableRow, selectedRows, feedback, text, bulkZahlartForm]);

  const saveBulkZahlart = async () => {
    const v = await bulkZahlartForm.validateFields();
    if (bulkZahlartSelected.length === 0) {
      feedback.warning(text("Please select at least one open item"));
      return;
    }
    setBulkZahlartSaving(true);
    try {
      await financeOperation.execute(async (committed) => {
      for (const item of bulkZahlartSelected) {
        const contributionId = item.sourceType === "contribution" ? item.sourceId : null;
        if (!contributionId) continue;
        // Nur Zahlungsart ändern – Betrag bewusst nicht senden (bei Teilzahlung gesperrt).
        await api.put(`/api/v1/contribution-calendar/${encodeURIComponent(contributionId)}`, {
          paymentMethod: Number(v.paymentMethod)
        });
        committed();
      }
      feedback.success(text("Payment method changed for contributions").replace("{0}", String(bulkZahlartSelected.length)));
      setBulkZahlartOpen(false);
      });
    } finally {
      setBulkZahlartSaving(false);
    }
  };

  const installmentsAllColumnsMap = React.useMemo<Record<string, ProColumnsWithMeasure<InstallmentRow>>>(
    () => ({
      paymentPlanName: { title: text("Payment plan"), dataIndex: "paymentPlanName", key: "paymentPlanName", width: PAYMENT_PLAN_NAME_COLUMN_WIDTH, ellipsis: false },
      installmentCount: { title: text("Number of installments"), dataIndex: "installmentCount", key: "installmentCount", width: INSTALLMENT_COUNT_COLUMN_WIDTH },
      totalAmount: {
        title: text("Total amount"),
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: TOTAL_AMOUNT_COLUMN_WIDTH,
        align: "right",
        render: (_, row) => formatMoney(Number(row.totalAmount || 0), units)
      },
      createdAt: {
        title: text("Created at"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: CREATED_AT_COLUMN_WIDTH,
        defaultSortOrder: "descend",
        render: (_, row) => formatDateTimeIso(String(row.createdAt || ""), units)
      }
    }),
    [text, units]
  );

  const openItemsColumnDefinitions: ColumnDefinition[] = [
    { key: "dueDate", label: text("Due date"), defaultVisible: true },
    { key: "amount", label: text("Amount"), defaultVisible: true },
    { key: "description", label: text("Description"), defaultVisible: true },
    { key: "paidAmount", label: text("Paid"), defaultVisible: true },
    { key: "openAmount", label: text("Open"), defaultVisible: true },
    { key: "paymentMethod", label: text("Payment method"), defaultVisible: true },
    { key: "status", label: text("Status"), defaultVisible: false },
  ];

  const openItemsActionsColumn: ListColumn<PersonOpenItemRow> = {
    title: "",
    key: "actions",
    width: ACTIONS_COLUMN_WIDTH,
    sorter: false,
    fixed: "right" as const,
    align: "right" as const,
    render: (_, row) => {
      if (!canWriteFinance) return null;
      // Gleiche Eignung wie der Bulk-„Zahlart ändern"-Filter – eine Quelle, keine Doppelung.
      const paymentLinkAction = payableItems.data?.includes(row.id) ? <MerchantPaymentLinkAction tr={text}
        create={() => api.post(`/api/v1/persons/${personId}/merchant-payment-links`, { subject: "OpenItem", subjectId: row.id })} /> : null;
      if (!isZahlartEditableRow(row)) return paymentLinkAction;
      const rowForDrawer: ContributionCalendarRow = {
        id: row.sourceType === "contribution" && row.sourceId ? row.sourceId : row.id,
        personId,
        contractPackageId: row.contractPackageId ?? "",
        contractName: row.contractPackageName ?? row.description ?? "",
        dueDate: row.dueDate,
        amount: Number(row.amount || 0),
        paidAmount: Number(row.paidAmount || 0),
        openAmount: Number(row.openAmount ?? row.amount ?? 0),
        billingCycle: 0,
        status: Number(row.status || 0),
        isManual: row.contributionIsManual === true,
        isRenewalPeriod: false,
        paymentId: row.paymentId ?? null,
        openItemId: row.id,
        paymentMethod: Number(row.paymentMethod || 0),
        bookedAt: row.bookedAt ?? null,
        createdAt: "",
        updatedAt: ""
      };
      return (
        <Flex wrap="wrap">
        {paymentLinkAction}
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            if (onOpenContributionEdit) {
              onOpenContributionEdit(rowForDrawer);
              return;
            }
            if (!setContributionEdit || !contributionEditForm || !setContributionEditOpen) return;
            setContributionEdit(rowForDrawer);
            contributionEditForm.setFieldsValue({ amount: Number(row.amount || 0), paymentMethod: Number(row.paymentMethod || 0) });
            setContributionEditOpen(true);
          }}
        >
          {text("Edit")}
        </Button>
        </Flex>
      );
    },
  };

  const openItemsAllColumnsMap: Record<string, ListColumn<PersonOpenItemRow>> = {
    dueDate: {
      title: text("Due date"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: DUE_DATE_COLUMN_WIDTH,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const dueCompare = String(a.dueDate || "").localeCompare(String(b.dueDate || ""));
        if (dueCompare !== 0) return dueCompare;
        return String(a.bookedAt || "").localeCompare(String(b.bookedAt || ""));
      },
      render: (_, r) => formatDateOnly(String(r.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
    },
    amount: {
      title: text("Amount"),
      dataIndex: "amount",
      key: "amount",
      width: AMOUNT_COLUMN_WIDTH,
      align: "right",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
      render: (_, r) => <ContributionCancellationAmount amount={Number(r.amount || 0)} cancelledAmount={r.cancelledAmount} />
    },
    description: {
      title: text("Description"),
      dataIndex: "description",
      key: "description",
      width: DESCRIPTION_COLUMN_WIDTH,
      ellipsis: false,
      sorter: (a, b) => String(a.contractPackageName || a.description || "").localeCompare(String(b.contractPackageName || b.description || "")),
      render: (_, r) => r.contractPackageName || r.description || text("Open item")
    },
    paidAmount: {
      title: text("Paid (status)"),
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: PAID_AMOUNT_COLUMN_WIDTH,
      align: "right",
      sorter: (a, b) => Number(a.paidAmount || 0) - Number(b.paidAmount || 0),
      render: (_, r) => formatMoney(Number(r.paidAmount || 0), units)
    },
    openAmount: {
      title: text("Open (status)"),
      dataIndex: "openAmount",
      key: "openAmount",
      width: OPEN_AMOUNT_COLUMN_WIDTH,
      align: "right",
      sorter: (a, b) => Number(a.openAmount || 0) - Number(b.openAmount || 0),
      render: (_, r) => formatOpenItemOpenAmount(r, text, units)
    },
    paymentMethod: {
      title: text("Payment method"),
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: PAYMENT_METHOD_COLUMN_WIDTH,
      sorter: (a, b) => Number(a.paymentMethod || 0) - Number(b.paymentMethod || 0),
      render: (_, r) => r.paymentMethod == null ? text("—") : paymentMethodLabel(Number(r.paymentMethod || 0), paymentMethodNameByType)
    },
    status: {
      title: text("Status"),
      dataIndex: "displayStatus",
      key: "status",
      width: STATUS_COLUMN_WIDTH,
      render: (_, r) => {
        if (Number(r.openAmount ?? r.amount ?? 0) <= MIN_PAYABLE_AMOUNT && r.displayStatus !== OPEN_ITEM_DISPLAY_STATUS.storno && r.displayStatus !== OPEN_ITEM_DISPLAY_STATUS.cancelled) {
          return <Tag>{text("—")}</Tag>;
        }
        if (r.displayStatus) {
          switch (r.displayStatus) {
            case OPEN_ITEM_DISPLAY_STATUS.paid:
              return <Tag color="green">{text("Paid (status)")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.cancelled:
              if (r.contributionIsManual === true && !r.cancelledAmount) return <Tag color="default">{text("Contribution manually waived")}</Tag>;
              return <Tag color="red">{text("Open item status cancelled")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.debtCollection:
              return <Tag color="purple">{text("Contribution calendar status debt collection")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.partialPaid:
              return <Tag color="gold">{text("Partially paid")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.storno:
              return <Tag color="purple">{text("Correction document")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.future:
              return <Tag>{text("Future")}</Tag>;
            case OPEN_ITEM_DISPLAY_STATUS.open:
            default: {
              // Ab Fälligkeit „fällig“; „mahnfällig“ erst nach der Mahnfrist der Regel (Kontoauszug, Liste, Übersicht).
              const effectiveStatus = contributionStatusFromDueDateForDisplay(r.dueDate, 0);
              if (effectiveStatus === CONTRIBUTION_STATUS_OVERDUE || effectiveStatus === CONTRIBUTION_STATUS_DUE) return <Tag color="orange">{text("Due")}</Tag>;
              return <Tag color="orange">{text("Open (status)")}</Tag>;
            }
          }
        }
        const status = Number(r.status || 0);
        if (status === CONTRIBUTION_STATUS_PAID) return <Tag color="green">{text("Paid (status)")}</Tag>;
        if (status === CONTRIBUTION_STATUS_CANCELLED) {
          if (r.contributionIsManual === true && !r.cancelledAmount) return <Tag color="default">{text("Contribution manually waived")}</Tag>;
          return <Tag color="red">{text("Open item status cancelled")}</Tag>;
        }
        if (status === CONTRIBUTION_STATUS_RESERVED) return <Tag color="blue">{text("Reserved")}</Tag>;
        if (status === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION) return <Tag color="purple">{text("Contribution calendar status debt collection")}</Tag>;
        return <Tag color="orange">{text("Open (status)")}</Tag>;
      }
    },
  };

  const hasSelectableOpenItems = visibleOpenItems.some((row) =>
    Number(row.status || 0) === OPEN_ITEM_STATUS_OPEN && Number(row.openAmount ?? row.amount ?? 0) > MIN_PAYABLE_AMOUNT
  );
  const handleVisibleOpenItemsChange = React.useCallback((rows: PersonOpenItemRow[]) => {
    setVisibleOpenItems((prev) => {
      if (prev.length === rows.length && prev.every((row, index) => String(row.id) === String(rows[index]?.id))) return prev;
      return rows;
    });
  }, []);

  return (
    <>
      <SettingsListPage<PersonOpenItemRow>
        toolbarRightExtra={(() => {
          const actions: React.ReactNode[] = [<OpenItemCancellationAction key="cancel" personId={personId} selectedOpenItemIds={selectedRows.map((row) => row.id)} onChanged={() => { void queryClient.invalidateQueries({ queryKey: ["person", personId] }); void openItemsRef.current?.revalidateCurrentQuery(); }} />];
          if (canAgreeInstallment) {
            actions.push(
              <Button
                key="installment"
                icon={<CreditCardOutlined />}
                onClick={async () => {
                  const res = await openItemsRequest();
                  const openOnly = (res.data || []).filter((r) => Number(r.openAmount ?? r.amount ?? 0) > MIN_PAYABLE_AMOUNT);
                  if (openOnly.length === 0) {
                    feedback.warning(text("No open items found for installment plan."));
                    return;
                  }
                  setOpenItemsInstallmentDrawerItems(openOnly);
                  const selectedIdsSet = new Set((selectedRows ?? []).map((row) => String(row.id)).filter((id) => id.length > 0));
                  setOpenItemsForInstallment(openOnly.filter((item) => selectedIdsSet.has(String(item.id))));
                  const initialValues = { paymentPlanId: null, installmentCount: null, startDate: dayjs().startOf("day") };
                  openItemsInstallmentForm.setFieldsValue(initialValues);
                  setOpenItemsInstallmentValues(initialValues);
                  setOpenItemsInstallmentOpen(true);
                }}
              >
                {text("Installment plan")}
              </Button>
            );
          }
          if (canWriteFinance) {
            actions.push(
              <Button
                key="bulk-zahlart"
                icon={<EditOutlined />}
                onClick={() => openBulkZahlart()}
              >
                {text("Change payment method (open contributions)")}
              </Button>
            );
          }
          if (canWriteFinance) {
            actions.push(
              <Button
                key="record-payment"
                type="primary"
                icon={<DollarOutlined />}
                onClick={() => {
                  const payables = selectedRows.filter((r) => Number(r.status || 0) === OPEN_ITEM_STATUS_OPEN && Number(r.openAmount ?? r.amount ?? 0) > MIN_PAYABLE_AMOUNT);
                  const items: PaymentItem[] = payables.map((openItem) => ({
                    id: openItem.id,
                    description: openItem.description || openItem.contractPackageName || text("Open item"),
                    dueDate: String(openItem.dueDate || "").slice(0, ISO_DATE_LENGTH),
                    openAmount: Number(openItem.openAmount ?? openItem.amount ?? 0),
                    paymentMethod: openItem.paymentMethod ?? null,
                    type: "openItem"
                  }));
                  setPaymentDrawerItems(items);
                  setPaymentDrawerSelectedItems(items);
                  setPaymentDrawerOpen(true);
                }}
              >
                {text("Record payment")}
              </Button>
            );
          }
          return actions.length > 0 ? (
            <Flex
              wrap="wrap"
              gap={8}
              justify="flex-end"
              align="center"
              style={{ width: isMobile ? "100%" : undefined, minWidth: 0 }}
            >
              {actions}
            </Flex>
          ) : undefined;
        })()}
        storageKey="persons.detail.openitems"
        rowKey="id"
        request={openItemsRequest}
        columnDefinitions={openItemsColumnDefinitions}
        allColumnsMap={openItemsAllColumnsMap}
        actionsColumn={openItemsActionsColumn}
        toolBarActions={[]}
        rowSelection={hasSelectableOpenItems ? {
          selectedRowKeys: (selectedRowKeys ?? []).map((key) => String(key)),
          onChange: (nextSelectedRowKeys, nextSelectedRows) => {
            setSelectedRowKeys(nextSelectedRowKeys.map((key) => String(key)));
            setSelectedRows(nextSelectedRows as PersonOpenItemRow[]);
          },
          getCheckboxProps: (row) => {
            const disabled = Number(row.status || 0) !== OPEN_ITEM_STATUS_OPEN;
            return { disabled, title: disabled ? text("Open item is paid or in SEPA run") : undefined };
          },
          // Checkbox-Spalte sichtbar inkl. „alle markieren"; Zeilenklick wählt zusätzlich.
          hideSelectAll: false,
          alwaysShowAlert: false,
        } : undefined}
        onVisibleRowsChange={handleVisibleOpenItemsChange}
        onRowClickForSelection={(row, event) => {
          const target = event.target as HTMLElement;
          if (target.closest("button") || target.closest("a") || target.closest(".ck-btn")) return;
          // Klick auf die Checkbox/Auswahlspalte nicht zusätzlich toggeln (sonst Doppel-Toggle).
          if (target.closest(".ck-checkbox-wrapper") || target.closest("input[type=\"checkbox\"]")) return;
          if (Number(row.status || 0) !== OPEN_ITEM_STATUS_OPEN) return;
          const key = String(row.id);
          if (selectedRowKeys.indexOf(key) > -1) {
            setSelectedRowKeys((prev) => prev.filter((itemKey) => String(itemKey) !== key));
            setSelectedRows((prev) => prev.filter((selectedRow) => String(selectedRow.id) !== key));
          } else {
            setSelectedRowKeys((prev) => [...prev, key]);
            setSelectedRows((prev) => [...prev, row]);
          }
        }}
        actionRef={openItemsRef}
        pagination={{
          pageSize: 20,
          showTotal: (total: number, range: [number, number]) => {
            if (selectedRows.length === 0) {
              return renderPaginationRange(text, total, range);
            }
            const totalAmount = selectedRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
            const totalPaid = selectedRows.reduce((sum, row) => sum + Number(row.paidAmount ?? 0), 0);
            const totalOpen = selectedRows.reduce((sum, row) => sum + Number(row.openAmount ?? row.amount ?? 0), 0);
            return (
              <Flex align="center" gap={16} wrap="nowrap" justify="flex-start" style={{ width: "100%" }}>
                <Flex align="center" gap={16} wrap="nowrap">
                  <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
                    {text("Amount")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalAmount, units)}</Typography.Text>
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
                    {text("Paid")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalPaid, units)}</Typography.Text>
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
                    {text("Open (status)")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalOpen, units)}</Typography.Text>
                  </Typography.Text>
                </Flex>
                <div style={{ flex: 1 }} />
                {renderPaginationRange(text, total, range)}
              </Flex>
            );
          },
        }}
      />

      {installments.length > 0 && (
        <div style={{ marginTop: cardDefaults.listCardHeaderMarginBottom }}>
          <SettingsListPage<InstallmentRow>
            headerTitle={<Typography.Title level={5} style={{ margin: 0 }}>{text("Installments")}</Typography.Title>}
            storageKey="persons.detail.installments"
            rowKey="id"
            dataSource={installments}
            columnDefinitions={INSTALLMENT_COLUMN_DEFINITIONS}
            allColumnsMap={installmentsAllColumnsMap}
            pagination={false}
          />
        </div>
      )}

      {!openItemsInstallmentOpen ? <Form form={openItemsInstallmentForm} style={{ display: "none" }} /> : null}
      <FormDrawer mode="edit" dirty={installmentDirty}
        title={text("Create installment plan")}
        open={openItemsInstallmentOpen}
        onCancel={() => {
          setOpenItemsInstallmentOpen(false);
          setOpenItemsForInstallment([]);
          setOpenItemsInstallmentDrawerItems([]);
          setOpenItemsInstallmentValues({});
        }}
        submitting={openItemsInstallmentSaving || financeOperation.pending}
        submitDisabled={
          openItemsForInstallment.length === 0
          || !openItemsInstallmentValues.paymentPlanId
          || !openItemsInstallmentValues.installmentCount
          || !openItemsInstallmentValues.startDate
        }
        onSave={() => saveOpenItemInstallment().catch(() => {})}
      >
        <Form
          form={openItemsInstallmentForm}
          layout="vertical"
          requiredMark={false}
          onValuesChange={(_, values) => setOpenItemsInstallmentValues(values)}
        >
          <DrawerSelectionList<PersonOpenItemRow>
            title={text("Select open items")}
            dataSource={openItemsInstallmentDrawerItems}
            rowKey="id"
            emptyText={<Typography.Text type="secondary">{text("No open items found for installment plan.")}</Typography.Text>}
            columns={[
              {
                title: text("Description"),
                dataIndex: "description",
                key: "description",
                render: (_, row) => row.description || row.contractPackageName || text("Open item")
              },
              {
                title: <span style={{ whiteSpace: "nowrap" }}>{text("Due date")}</span>,
                dataIndex: "dueDate",
                key: "dueDate",
                width: 120,
                render: (_, row) => formatDateOnly(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
              },
              {
                title: text("Open (status)"),
                dataIndex: "openAmount",
                key: "openAmount",
                width: 100,
                align: "right",
                render: (_, row) => formatOpenItemOpenAmount(row, text, units)
              }
            ]}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: openItemsForInstallment.map((row) => row.id),
              onChange: (_keys, rows) => setOpenItemsForInstallment(rows as PersonOpenItemRow[]),
              hideSelectAll: false,
              selectionByRowClick: true
            }}
          />
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="paymentPlanId" label={text("Payment plan")} rules={[{ required: true, message: text("Required") }]}>
                <Select
                  style={{ width: "100%" }}
                  placeholder={text("Select plan")}
                  options={paymentPlans.map((plan) => ({ value: plan.id, label: plan.name }))}
                  onChange={(paymentPlanId) => {
                    const nextValues = {
                      ...openItemsInstallmentValues,
                      paymentPlanId: paymentPlanId ? String(paymentPlanId) : null,
                      installmentCount: DEFAULT_INSTALLMENT_COUNT
                    };
                    openItemsInstallmentForm.setFieldsValue(nextValues);
                    setOpenItemsInstallmentValues(nextValues);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="installmentCount" label={text("Number of installments")} rules={[{ required: true, message: text("Required") }]}>
                <InputNumber key={openItemsWatchedPlanId ?? "no-plan"} controls style={{ width: "100%" }} min={2} max={24} step={1} />
              </Form.Item>
            </Col>
          </Row>
          {openItemsSelectedPlan && openItemsForInstallment.length > 0 && openItemsInstallmentPreview && (
            <Form.Item label={text("Preview")}>
              <div style={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: designBorderRadius.xl, padding: spacing.md, background: "rgba(0,0,0,0.015)" }}>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: spacing.sm }}>
                  {text("Financial breakdown")}
                </Typography.Text>
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "base",
                      label: openItemsInstallmentPreview.selectedItemCount > 1 ? text("Base amount (selected open items)") : text("Base amount (open item)"),
                      children: formatMoney(openItemsInstallmentPreview.basePrice, units)
                    },
                    { key: "surcharge", label: text("Surcharge"), children: openItemsInstallmentPreview.surchargeLabel },
                    { key: "cycle", label: text("Payment cycle"), children: openItemsInstallmentPreview.cycle },
                    {
                      key: "installment",
                      label: openItemsInstallmentPreview.count > 1 ? text("Price per installment") : text("Price"),
                      children: formatMoney(openItemsInstallmentPreview.count > 1 ? openItemsInstallmentPreview.installment : openItemsInstallmentPreview.total, units)
                    },
                    { key: "total", label: text("Total"), children: formatMoney(openItemsInstallmentPreview.total, units) }
                  ]}
                />
              </div>
            </Form.Item>
          )}
          <Form.Item name="startDate" label={text("Start date")} rules={[{ required: true, message: text("Required") }]} initialValue={dayjs().startOf("day")}>
            <DatePicker allowClear={false} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </FormDrawer>

      {!bulkZahlartOpen ? <Form form={bulkZahlartForm} style={{ display: "none" }} /> : null}
      <FormDrawer mode="edit" dirty={paymentMethodDirty}
        title={text("Change payment method (open contributions)")}
        open={bulkZahlartOpen}
        onCancel={() => setBulkZahlartOpen(false)}
        submitting={bulkZahlartSaving || financeOperation.pending}
        submitDisabled={bulkZahlartSelected.length === 0 || !bulkWatchedPaymentMethod}
        onSave={() => saveBulkZahlart().catch(() => {})}
      >
        <Form form={bulkZahlartForm} layout="vertical" requiredMark={false}>
          <DrawerSelectionList<PersonOpenItemRow>
            title={text("Select contributions")}
            dataSource={bulkZahlartItems}
            rowKey="id"
            emptyText={<Typography.Text type="secondary">{text("No open contributions to change payment method")}</Typography.Text>}
            columns={[
              {
                title: text("Description"),
                dataIndex: "description",
                key: "description",
                render: (_, row) => row.contractPackageName || row.description || text("Open item")
              },
              {
                title: <span style={{ whiteSpace: "nowrap" }}>{text("Due date")}</span>,
                dataIndex: "dueDate",
                key: "dueDate",
                width: 120,
                render: (_, row) => formatDateOnly(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
              },
              {
                title: text("Open (status)"),
                dataIndex: "openAmount",
                key: "openAmount",
                width: 100,
                align: "right",
                render: (_, row) => formatOpenItemOpenAmount(row, text, units)
              },
              {
                title: text("Payment method"),
                dataIndex: "paymentMethod",
                key: "paymentMethod",
                width: 140,
                render: (_, row) => row.paymentMethod == null ? text("—") : paymentMethodLabel(Number(row.paymentMethod || 0), paymentMethodNameByType)
              }
            ]}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: bulkZahlartSelected.map((row) => row.id),
              onChange: (_keys, rows) => setBulkZahlartSelected(rows as PersonOpenItemRow[]),
              hideSelectAll: false,
              selectionByRowClick: true
            }}
          />
          <Form.Item
            name="paymentMethod"
            label={text("New payment method")}
            rules={[{ required: true, message: text("Payment method is required") }]}
          >
            <Select
              options={paymentMethodDefinitionSelectOptions(paymentMethodDefs)}
              placeholder={text("Select payment method…")}
            />
          </Form.Item>
        </Form>
      </FormDrawer>
    </>
  );
}
